import {
  canMerge,
  canPlanetUpgrade,
  isMaxed,
  mergeCost,
  planetUpgradeCost,
  sellValue,
  upgradeCost,
} from '@playnets/sim';
import { PLANET_UPGRADES } from '@playnets/data';
import { useUi } from '../store';
import { StructureIcon, UpgradeIcon } from './Icon';
import type { GameHandle } from '../game/useGame';

/**
 * A small dark dialog for the two things a mount can do once something is on
 * it — upgrade, sell — plus the planet's own powers.
 *
 * Choosing *what to build* used to live here too, and doesn't any more: it's
 * the build bar, which needs no dialog at all. What's left is genuinely modal
 * work on one thing you already own, and it's rare enough that dimming the
 * board for it is fine.
 */
export default function BuildSheet({ game }: { game: GameHandle | null }): JSX.Element | null {
  const sheet = useUi((s) => s.sheet);
  if (sheet === null || game === null) return null;

  const close = (): void => useUi.getState().openSheet(null);

  return (
    <div className="scrim" onPointerDown={close}>
      <div className="dialog" role="dialog" onPointerDown={(e) => e.stopPropagation()}>
        {sheet.kind === 'planet' ? (
          <PlanetPowers game={game} />
        ) : (
          <MountOptions game={game} slotId={sheet.id} />
        )}
      </div>
    </div>
  );
}

function PlanetPowers({ game }: { game: GameHandle }): JSX.Element {
  const hud = useUi((s) => s.hud);
  const offered = PLANET_UPGRADES.filter(
    (u) => canPlanetUpgrade(game.world, u).reason !== 'not-here',
  );

  return (
    <>
      <div className="tiles">
        {offered.map((u) => {
          const level = hud.upgrades[u.id] ?? 0;
          const maxed = level >= u.costs.length;
          const check = canPlanetUpgrade(game.world, u);
          return (
            <button
              key={u.id}
              className={`tile${level > 0 ? ' is-owned' : ''}`}
              disabled={!check.ok}
              onClick={() => game.input.push({ kind: 'planet-upgrade', upgrade: u.id })}
              title={`${u.name} — ${u.text}`}
            >
              <UpgradeIcon id={u.id} />
              <span className="tile-name">{u.name}</span>
              <span className="tile-cost">{maxed ? '✓' : planetUpgradeCost(u, level)}</span>
              <span className="pips">
                {u.costs.map((_, i) => (
                  <i key={i} className={i < level ? 'is-on' : ''} />
                ))}
              </span>
            </button>
          );
        })}
      </div>
    </>
  );
}

function MountOptions({ game, slotId }: { game: GameHandle; slotId: number }): JSX.Element | null {
  const hud = useUi((s) => s.hud);
  const slot = game.world.slots[slotId];
  if (!slot) return null;

  const structure = slot.structure;
  const def = structure ? game.content.structures.get(structure.defId) : undefined;
  const close = (): void => useUi.getState().openSheet(null);

  // An empty mount has nothing to offer here — you fill it from the bar.
  if (!def || !structure) return null;

  const maxed = isMaxed(def, structure.tier);
  const merge = canMerge(game.world, game.content, slot, def.id);
  const into = def.mergesInto ? game.content.structures.get(def.mergesInto) : undefined;
  return (
    <>
      <div className="tiles">
        {/*
          Once the ladder is done, the upgrade tile has nothing left to offer,
          so its place goes to what does. The gesture is dragging a matching
          card onto the mount; this is the same act with a button on it, for
          the player who tapped the gun to find out what it can still do.
        */}
        {maxed && into ? (
          <button
            className="tile is-merge"
            disabled={!merge.ok}
            onClick={() => {
              game.input.push({ kind: 'merge', slot: slot.id, structure: def.id });
              close();
            }}
            title={`${into.name} — ${into.text}`}
          >
            <StructureIcon def={into} />
            <span className="tile-name">{into.name}</span>
            <span className="tile-cost">{mergeCost(def)}</span>
          </button>
        ) : (
          <button
            className="tile"
            disabled={maxed || hud.juice < upgradeCost(def, structure.tier)}
            onClick={() => game.input.push({ kind: 'upgrade', slot: slot.id })}
            title="Upgrade"
          >
            <svg viewBox="0 0 32 32" className="icon" aria-hidden="true">
              <path d="M16 4 L28 18 H21 V28 H11 V18 H4 Z" fill="var(--hot)" />
            </svg>
            <span className="tile-name">Upgrade</span>
            <span className="tile-cost">{maxed ? '✓' : upgradeCost(def, structure.tier)}</span>
          </button>
        )}
        {/* Free between waves, 70% mid-wave. */}
        <button
          className="tile"
          onClick={() => {
            game.input.push({ kind: 'sell', slot: slot.id });
            close();
          }}
          title={hud.building ? 'Recycle' : 'Sell'}
        >
          <svg viewBox="0 0 32 32" className="icon" aria-hidden="true">
            <path
              d="M9 6 A11 11 0 1 1 6 17"
              fill="none"
              stroke="var(--paper)"
              strokeWidth="4"
              strokeLinecap="round"
            />
            <path d="M3 3 L12 6 L5 12 Z" fill="var(--paper)" />
          </svg>
          <span className="tile-name">{hud.building ? 'Recycle' : 'Sell'}</span>
          <span className="tile-cost">+{sellValue(structure.spent, hud.building)}</span>
        </button>
      </div>
    </>
  );
}
